import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  DoorOpen,
  LayoutDashboard,
  Plus,
  Settings,
  Users,
  User,
  ShieldCheck,
  BarChart3,
  Trash2
} from 'lucide-react';
import * as React from 'react';

import { ActionBarDialog } from '~/components/action-bar-dialog';
import { api } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { usePresentationMode } from '~/lib/presentation-mode';
import { ROOM_CREATE_PATH } from '~/lib/room-routes';

export type ActionBarAction = {
  id: string;
  label: string;
  group?: string;
  keywords?: string[];
  icon?: React.ComponentType<{ className?: string }>;
  onSelect: () => void | Promise<void>;
  /**
   * When false, action is hidden.
   * Prefer this over conditionally registering actions to avoid flicker.
   */
  enabled?: boolean;
};

type RegisteredActionsEntry = {
  key: symbol;
  actions: ActionBarAction[];
};

type ActionBarContextValue = {
  open: () => void;
  close: () => void;
  toggle: () => void;
  register: (actions: ActionBarAction[]) => () => void;
};

const ActionBarContext = React.createContext<ActionBarContextValue | null>(
  null
);

export function ActionBarProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { user } = useAuth();
  const { presentationMode } = usePresentationMode();
  const [open, setOpen] = React.useState(false);
  const [registered, setRegistered] = React.useState<RegisteredActionsEntry[]>(
    []
  );
  const isAdminOrOwner = user?.role === 'owner' || user?.role === 'admin';
  const effectivePresentationMode = presentationMode && isAdminOrOwner;

  const { data: roomsData } = useQuery({
    queryKey: ['rooms'],
    queryFn: () =>
      api.get<{ rooms: Array<{ id: string; name: string }> }>('/api/rooms'),
    enabled: !!user && !effectivePresentationMode
  });

  const rooms = roomsData?.rooms ?? [];

  const baseActions = React.useMemo<ActionBarAction[]>(() => {
    const actions: ActionBarAction[] = [
      {
        id: 'nav.dashboard',
        label: 'Go to Dashboard',
        group: 'Navigation',
        keywords: ['home'],
        icon: LayoutDashboard,
        onSelect: () => router.navigate({ to: '/dashboard' }),
        enabled: !!user
      },
      {
        id: 'nav.rooms',
        label: 'Go to Rooms',
        group: 'Navigation',
        keywords: ['room list'],
        icon: DoorOpen,
        onSelect: () => router.navigate({ to: '/rooms' }),
        enabled: !!user
      },
      {
        id: 'nav.reports',
        label: 'Go to Reports',
        group: 'Navigation',
        keywords: ['analytics', 'activity'],
        icon: BarChart3,
        onSelect: () => router.navigate({ to: '/reports' }),
        enabled: isAdminOrOwner
      },
      {
        id: 'nav.bin',
        label: 'Go to Bin',
        group: 'Navigation',
        keywords: ['trash', 'deleted', 'restore'],
        icon: Trash2,
        onSelect: () => router.navigate({ to: '/bin' }),
        enabled: isAdminOrOwner
      },
      {
        id: 'room.create',
        label: 'Create room',
        group: 'Rooms',
        keywords: ['new', 'add'],
        icon: Plus,
        onSelect: () => router.navigate({ to: ROOM_CREATE_PATH }),
        enabled: isAdminOrOwner
      },
      {
        id: 'nav.settings.profile',
        label: 'Settings: Profile',
        group: 'Settings',
        keywords: ['account'],
        icon: User,
        onSelect: () => router.navigate({ to: '/settings/profile' }),
        enabled: !!user
      },
      {
        id: 'nav.settings.users',
        label: 'Settings: Users',
        group: 'Settings',
        icon: Users,
        onSelect: () => router.navigate({ to: '/settings/users' }),
        enabled: isAdminOrOwner
      },
      {
        id: 'nav.settings.groups',
        label: 'Settings: Groups',
        group: 'Settings',
        keywords: ['teams', 'roles'],
        icon: ShieldCheck,
        onSelect: () => router.navigate({ to: '/settings/groups' }),
        enabled: isAdminOrOwner
      },
      {
        id: 'nav.settings',
        label: 'Open Settings',
        group: 'Settings',
        keywords: ['preferences'],
        icon: Settings,
        onSelect: () => router.navigate({ to: '/settings' }),
        enabled: !!user
      }
    ];

    if (!effectivePresentationMode) {
      for (const room of rooms) {
        actions.push({
          id: `room.open.${room.id}`,
          label: `Open room: ${room.name}`,
          group: 'Rooms',
          keywords: ['open', 'switch', room.id],
          icon: DoorOpen,
          onSelect: () =>
            router.navigate({
              to: '/rooms/$roomId',
              params: { roomId: room.id }
            }),
          enabled: !!user
        });
      }
    }

    return actions;
  }, [effectivePresentationMode, rooms, router, user, isAdminOrOwner]);

  const allActions = React.useMemo(() => {
    const extra = registered.flatMap((e) => e.actions);
    return [...baseActions, ...extra];
  }, [baseActions, registered]);

  const close = React.useCallback(() => setOpen(false), []);
  const openFn = React.useCallback(() => setOpen(true), []);
  const toggle = React.useCallback(() => setOpen((v) => !v), []);

  const register = React.useCallback((actions: ActionBarAction[]) => {
    const key = Symbol('action-bar-actions');
    setRegistered((prev) => [...prev, { key, actions }]);
    return () => {
      setRegistered((prev) => prev.filter((e) => e.key !== key));
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key.toLowerCase() !== 'k') return;
      const isMac =
        typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
      const comboPressed = isMac ? e.metaKey : e.ctrlKey;
      if (!comboPressed) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const value = React.useMemo<ActionBarContextValue>(
    () => ({ open: openFn, close, toggle, register }),
    [close, openFn, register, toggle]
  );

  return (
    <ActionBarContext.Provider value={value}>
      {children}
      <ActionBarDialog
        open={open}
        onOpenChange={setOpen}
        actions={allActions}
        hideSearchResults={effectivePresentationMode}
        onActionSelected={() => setOpen(false)}
      />
    </ActionBarContext.Provider>
  );
}

export function useActionBar() {
  const ctx = React.useContext(ActionBarContext);
  if (!ctx) {
    throw new Error('useActionBar must be used within ActionBarProvider');
  }
  return ctx;
}

/**
 * Register actions while a component is mounted.
 * Usage:
 *   useRegisterActionBarActions(() => [...], [deps])
 */
export function useRegisterActionBarActions(
  getActions: () => ActionBarAction[],
  deps: React.DependencyList
) {
  const ctx = React.useContext(ActionBarContext);

  React.useEffect(() => {
    if (!ctx) return;
    const cleanup = ctx.register(getActions());
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
